/**
 * How a GitHub link attempt came back, read once out of the address bar.
 *
 * The server sends a code and the copy lives here, for the same reason
 * `AuthPanel` does it that way: the value arrives in a query string, and
 * anyone can put anything in one. An unrecognised code falls through to the
 * generic line, so nothing a stranger types can be rendered.
 */
export interface GithubLinkOutcome {
  ok: boolean
  message: string
}

const OUTCOMES: Record<string, GithubLinkOutcome> = {
  linked: { ok: true, message: 'GitHub linked. You can sign in with it from now on.' },
  taken: {
    ok: false,
    message: 'That GitHub account is already linked to another stagepost account. Unlink it there first.',
  },
  failed: { ok: false, message: 'GitHub did not finish that. Nothing changed — try again.' },
}

const GENERIC: GithubLinkOutcome = { ok: false, message: 'That did not work. Try again.' }

/**
 * Read `?github_link=`, then take it out of the address bar.
 *
 * Left in place, a refresh re-announces a link that happened ten minutes ago,
 * and the parameter follows the person around if they bookmark the page.
 */
export function consumeGithubLink(): GithubLinkOutcome | null {
  if (typeof window === 'undefined') return null
  const found = new URLSearchParams(window.location.search).get('github_link')
  if (!found) return null

  const url = new URL(window.location.href)
  url.searchParams.delete('github_link')
  window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`)

  return OUTCOMES[found] ?? GENERIC
}
